import { useCallback, useEffect, useMemo, useState } from "react";
import type { ServiceTicket, ServiceTicketStatus } from "@/data/serviceTicketTypes";
import {
  ResolveServicePageService,
  type PageResponse,
  type ResolveServiceJobOption,
  type ResolveServiceSummary,
  type ResolveServiceTab,
  type ResolveServiceTicketsPageQuery,
} from "@/services/resolve-service-page.service";

function emptyPage<T>(): PageResponse<T> {
  return { data: [], count: 0, error: null };
}

export function useResolveServiceTicketsPage(query: ResolveServiceTicketsPageQuery & { enabled?: boolean }) {
  const [state, setState] = useState<PageResponse<ServiceTicket>>(emptyPage());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setState(emptyPage());
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    void ResolveServicePageService.getTicketsPage(query).then((result) => {
      if (cancelled) return;
      setState(result);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [
    query.enabled,
    query.page,
    query.pageSize,
    query.refreshToken,
    query.search,
    query.status,
    query.tab,
  ]);

  return { ...state, loading };
}

export function useResolveServiceSummary(query: { enabled?: boolean; refreshToken?: number }) {
  const [data, setData] = useState<ResolveServiceSummary | null>(null);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setData(null);
      setError(null);
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    void ResolveServicePageService.getSummary(query.refreshToken).then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [query.enabled, query.refreshToken]);

  return { data, error, loading };
}

export function useResolveServiceJobOptions(query: { enabled?: boolean; search?: string; limit?: number; refreshToken?: number }) {
  const [state, setState] = useState<PageResponse<ResolveServiceJobOption>>(emptyPage());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setState(emptyPage());
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    void ResolveServicePageService.getJobOptions(query).then((result) => {
      if (cancelled) return;
      setState(result);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [query.enabled, query.limit, query.refreshToken, query.search]);

  return { ...state, loading };
}

export function useResolveServiceServerData(query: {
  enabled?: boolean;
  page?: number;
  pageSize?: number;
  search?: string;
  status?: ServiceTicketStatus | "all";
  tab?: ResolveServiceTab;
}) {
  const [refreshToken, setRefreshToken] = useState(0);
  const page = useResolveServiceTicketsPage({
    enabled: query.enabled,
    page: query.page ?? 0,
    pageSize: query.pageSize ?? 25,
    search: query.search,
    status: query.status,
    tab: query.tab ?? "active",
    refreshToken,
  });
  const summary = useResolveServiceSummary({ enabled: query.enabled, refreshToken });

  const tickets = useMemo(() => page.data, [page.data]);
  const refresh = useCallback(() => {
    setRefreshToken((value) => value + 1);
  }, []);

  return {
    count: page.count,
    error: page.error ?? summary.error,
    loading: page.loading || summary.loading,
    refresh,
    refreshToken,
    summary: summary.data,
    tickets,
  };
}
